import { isArray } from '@planjs/utils';
import type { Moment } from 'moment';

type OptionValue = string | number | string[] | number[] | boolean | Moment | Moment[];
type OptionsMap = Record<string, OptionValue>;

/**
 * 处理 listOptions
 */
class ListOptions {
  protected options: core.ListOption_Option[] = [];

  /**
   * 目前listOption传给后端都是这个结构
   */
  get reqParams() {
    return {
      options: this.options,
    };
  }

  /**
   * 校验option
   * @param option
   */
  static validOption = (option: core.ListOption_Option): core.ListOption_Option | void => {
    if (
      !option ||
      option.value === '' ||
      option.value === undefined ||
      option.value === null ||
      option.type === null ||
      option.type === undefined
    ) {
      return;
    }
    return option;
  };

  /**
   * 对象转换成list_options
   * @param map
   * @param fn
   */
  static convertMapToOptions(
    map: OptionsMap,
    fn?: (key: string, value: OptionValue) => core.ListOption_Option | void,
  ): core.ListOption_Option[] {
    // 一些时间相关的，值用moment类型，可以直接转换成后端需要的 秒级时间戳
    function processMomentDate(value: OptionValue) {
      // TODO CHECK 这里应该 instanceof 判断原型链, 现在没有伪造的情况
      function isMoment(v: any) {
        return typeof v === 'object' && v !== null && (v as any)._isAMomentObject;
      }
      try {
        if (isMoment(value)) {
          return (value as Moment).format('X');
        }
        if (value instanceof Array) {
          return (value as any[]).map((v) => (isMoment(v) ? (v as Moment).format('X') : v));
        }
      } catch (e) {}
      return value;
    }

    return Object.keys(map)
      .reduce((options: core.ListOption_Option[], k) => {
        let option: core.ListOption_Option = {
          type: +k,
          // 去掉不正常的值
          value:
            map[k] !== '' &&
            map[k] !== undefined &&
            map[k] !== null &&
            (typeof map[k] === 'number' ? !isNaN(map[k] as number) : 1) &&
            map[k] !== 'undefined' &&
            map[k] !== 'null' &&
            map[k] !== 'NaN'
              ? String(processMomentDate(map[k])).trim()
              : '',
        };
        if (fn) {
          option = fn(k, map[k]) as core.ListOption_Option;
        }
        if (ListOptions.validOption(option)) {
          options.push(option);
        }
        return options;
      }, [])
      .sort((a, b) => a.type! - b.type!);
  }

  /**
   * 转换listOptions to object
   * @param options
   */
  static convertOptionsToMap(options: core.ListOption_Option[]): OptionsMap {
    return options.reduce((acc: { [type: string]: string }, item) => {
      acc[item.type!] = item.value!;
      return acc;
    }, {});
  }

  /**
   * 初始化
   * @param v
   */
  static create = (v?: OptionsMap | core.ListOption_Option[] | object): ListOptions => {
    if (!v) {
      return new ListOptions();
    }
    if (isArray(v)) {
      return new ListOptions().setOptions(v);
    }
    return new ListOptions().mapToOptions(v as OptionsMap);
  };

  /**
   * 设置options，无效的会过滤
   * @param options
   */
  setOptions = (options: core.ListOption_Option[]): this => {
    this.options = options.filter(ListOptions.validOption);
    return this;
  };

  /**
   * 删除指定 type 的 option
   * @param types
   */
  deleteOption = (...types: number[]): this => {
    this.setOptions(this.options.filter((v: core.ListOption_Option) => !types.includes(v.type!)));
    return this;
  };

  /**
   * 删除指定 value 的 option
   * @param values
   */
  deleteOptionValue = (...values: OptionValue[]): this => {
    this.setOptions(this.options.filter((v: core.ListOption_Option) => !values.includes(v.value!)));
    return this;
  };

  /**
   * 只保留指定 type 的 option
   * @param types
   */
  deleteOptionNotEq = (...types: number[]): this => {
    this.setOptions(this.options.filter((v: core.ListOption_Option) => types.includes(v.type!)));
    return this;
  };

  /**
   * 增加 list_options options
   * @param opts
   */
  appendOption = (...opts: core.ListOption_Option[]): this => {
    this.setOptions([...this.options, ...opts]);
    return this;
  };

  /**
   * 存在则替换， 不存在则新增
   * @param options
   */
  saveOption = (...options: core.ListOption_Option[]): this => {
    options.forEach((option) => {
      if (ListOptions.validOption(option)) {
        this.deleteOption(option.type!);
        this.appendOption(option);
      }
    });
    return this;
  };

  /**
   * 通过object替换并且添加option
   * @param map
   * @param fn
   */
  mapToOptions = (
    map: OptionsMap,
    fn?: Parameters<typeof ListOptions.convertMapToOptions>[1],
  ): this => {
    this.setOptions(ListOptions.convertMapToOptions(map, fn));
    return this!;
  };
}

export default ListOptions;
